/**
 * Numbered markers for the records linked to a sheet revision (spec §12, slice 3). Kept free of Angular so the maths,
 * filters, grouping and routes are unit-testable.
 */
import { formatDate } from '@angular/common';
import { DrawingLinkedDay, DrawingLinkedRecord } from '../../../../../../../shared/models/drawing-sheet';
import { Markup, MarkupLinkedRecord } from '../../../../../../../shared/models/markup';

/** Zoom steps of the read-only sheet (the T9 sheet picker uses the same). */
export const SHEET_ZOOMS = [1, 1.5, 2, 3];
/**
 * Place on map's sheet (Sri): station labels on a 22×34 sheet need more than 3×. The dialog draws the part in view sharp
 * from the PDF at every zoom (sheetRegion); the viewer and compare keep SHEET_ZOOMS.
 */
export const PLACE_SHEET_ZOOMS = [1, 1.5, 2, 3, 4, 6, 8];

/** The /linked page size the markers load in one call (the API maximum). */
export const MARKER_CAP = 200;

/** Record-mark overlays (tagged rows, one full-page PNG each) drawn at once at most (W25). */
export const RECORD_MARKS_CAP = 50;

const NOUNS: Record<string, string> = {
  issue: 'Issue',
  rfi: 'RFI',
  punchlist_item: 'Punch item',
  warranty_claim: 'Warranty claim',
  report_bid_item: 'Bid item',
  report_material_delivery: 'Delivery',
  report_activity: 'Activity',
  report_internal_test: 'Test',
  report_form_header: 'Form',
  report_activity_code_header: 'Activity codes',
  daily_diary_remark: 'Diary remark',
};

const CLOSED_STATUSES = new Set(['closed', 'resolved', 'completed', 'void', 'cancelled', 'canceled', 'approved']);

/**
 * Where a marker sits on the rendered page, in px from its top-left corner. Points are in the preview image's space
 * (R19), so the same point lands on the same spot at any zoom: the page is simply rendered bigger.
 */
export function markerPosition(point: { x: number; y: number }, size: { width: number; height: number }): { left: number; top: number } {
  return { left: point.x * size.width, top: point.y * size.height };
}

/** Red while the record is open; ink once closed, and for records that have no status. */
export function isOpenStatus(status: string | null | undefined): boolean {
  return typeof status === 'string' && !!status.trim() && !CLOSED_STATUSES.has(status.trim().toLowerCase());
}

export function linkedRecordNoun(entityType: string): string {
  return NOUNS[entityType] ?? 'Record';
}

/**
 * W54: the viewer's record filter, from its Filter (N): the record types shown (null: every type) and Mine only. It
 * replaces the single-choice chips (All / Mine / Bid items / …); several types can show at once.
 */
export interface RecordFilter {
  types: string[] | null;
  mineOnly: boolean;
}

export const ALL_RECORDS: RecordFilter = { types: null, mineOnly: false };

/** The record types the Records section offers, in this order; a type linked on the sheet that is not here joins them */
export const RECORD_TYPE_LABELS: { value: string; label: string }[] = [
  { value: 'report_bid_item', label: 'Bid items' },
  { value: 'report_material_delivery', label: 'Deliveries' },
  { value: 'report_activity', label: 'Activities' },
  { value: 'report_internal_test', label: 'Tests' },
  { value: 'report_form_header', label: 'Forms' },
  { value: 'report_activity_code_header', label: 'Activity codes' },
  { value: 'daily_diary_remark', label: 'Diary remarks' },
  { value: 'issue', label: 'Issues' },
  { value: 'rfi', label: 'RFIs' },
  { value: 'punchlist_item', label: 'Punch items' },
  { value: 'warranty_claim', label: 'Warranty' },
];

/** The filter shows every record: no types left out and not Mine only */
export function isAllRecords(filter: RecordFilter): boolean {
  return filter.types === null && !filter.mineOnly;
}

export function recordMatches(row: DrawingLinkedRecord, filter: RecordFilter, accountUuid: string | null): boolean {
  if (filter.types !== null && !filter.types.includes(row.entity_type)) {
    return false;
  }
  return !filter.mineOnly || (!!accountUuid && row.created_by?.uuid === accountUuid);
}

/**
 * Overlay rows for a record filter. Every record keeps every row (untagged, carried, and tagged rows whose record the
 * caller cannot list); a narrower filter keeps only the tagged rows of the records it matches.
 */
export function filterLayersBy(layers: Markup[], filter: RecordFilter, rows: DrawingLinkedRecord[], accountUuid: string | null): Markup[] {
  if (isAllRecords(filter)) {
    return layers;
  }
  const kept = new Set(rows.filter(r => recordMatches(r, filter, accountUuid)).map(r => r.uuid));
  return layers.filter(l => !!l.link_uuid && kept.has(l.link_uuid));
}

export interface LinkedDayGroup {
  date: string;
  /** Links on that day across every page, not just this one. */
  count: number;
  rows: DrawingLinkedRecord[];
}

/**
 * Files one page of rows under the API's `meta.days`. Rows and days are both newest first and the counts cover every
 * row, so the row at absolute index `start + i` belongs to the day whose running total first exceeds that index. This
 * keeps the project's timezone, which the rows' timestamps alone would not.
 */
export function groupLinkedByDay(rows: DrawingLinkedRecord[], days: DrawingLinkedDay[], start: number): LinkedDayGroup[] {
  const groups: LinkedDayGroup[] = [];
  let day = 0;
  let upTo = days[0]?.count ?? 0;
  rows.forEach((row, i) => {
    const index = start + i;
    while (day < days.length - 1 && index >= upTo) {
      day++;
      upTo += days[day].count;
    }
    const date = days[day]?.date ?? row.created_at?.slice(0, 10) ?? '';
    const last = groups[groups.length - 1];
    if (last?.date === date) {
      last.rows.push(row);
    } else {
      groups.push({ date, count: days[day]?.count ?? 1, rows: [row] });
    }
  });
  return groups;
}

export interface RecordRoute {
  commands: string[];
  queryParams: Record<string, string>;
}

/** The record's own page, as global search opens it; null when a needed parent is missing. */
export function linkedRecordRoute(projectId: string, row: DrawingLinkedRecord): RecordRoute | null {
  const project = ['/projects', projectId];
  const id = row.entity_uuid;
  switch (row.entity_type) {
    case 'issue':
      return { commands: project, queryParams: { tab: 'issues', status: 'all', selectedId: id } };
    case 'rfi':
      return { commands: project, queryParams: { tab: 'rfis', rifId: id } };
    case 'punchlist_item':
      return row.punchlist_uuid
        ? { commands: project, queryParams: { tab: 'punchlist', subTab: 'items', punchlistId: row.punchlist_uuid, itemId: id } }
        : null;
    case 'warranty_claim':
      return { commands: project, queryParams: { tab: 'warranty', subTab: 'claims', claimId: id } };
    case 'daily_diary_remark':
      return row.site_uuid && row.report_date
        ? { commands: [...project, 'sites', row.site_uuid], queryParams: { tab: 'daily-report', date: row.report_date } }
        : null;
    default:
      if (row.entity_type?.startsWith('report_') && row.site_uuid && row.report_date && row.daily_report_uuid) {
        return {
          commands: [...project, 'sites', row.site_uuid],
          queryParams: { tab: 'daily-report', date: row.report_date, reportId: row.daily_report_uuid },
        };
      }
      return null;
  }
}

/**
 * 7.2: an as-built mark made from a record: "From <label>", "From a deleted <record>" once the record is gone, or
 * "From a <record>" when the reader may not open it; '' for a mark not made from a record.
 */
export function markRecordText(record: MarkupLinkedRecord | null | undefined): string {
  if (!record) return '';
  const noun = linkedRecordNoun(record.entity_type);
  const lower = noun === 'RFI' ? noun : noun.toLowerCase();
  if (record.deleted) return `From a deleted ${lower}`;
  return record.label ? `From ${record.label}` : `From ${noun === 'RFI' ? 'an' : 'a'} ${lower}`;
}

/**
 * 7.2: where "From <record>" goes: the Linked here route, from the mark's record and its `parent` (punch list, report or
 * diary, warranty: the same keys as a Linked here row); null when deleted, hidden or not routable.
 */
export function markRecordRoute(projectId: string, record: MarkupLinkedRecord | null | undefined): RecordRoute | null {
  if (!record?.uuid || record.deleted || !projectId) return null;
  return linkedRecordRoute(projectId, { entity_type: record.entity_type, entity_uuid: record.uuid, ...(record.parent ?? {}) } as DrawingLinkedRecord);
}

/**
 * A calendar date ("2026-10-05": a `meta.days` key, a revision's issued_at) as "Oct 5, 2026". It is built from its
 * parts, so no timezone can move it to another day; a time part after the date is ignored.
 */
export function dayTitle(date: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date || '');
  if (!match) {
    return date || '';
  }
  const [y, m, d] = match.slice(1).map(Number);
  return formatDate(new Date(y, m - 1, d), 'MMM d, y', 'en-US');
}
